import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { BooleanFilter } from "components/NewAdvancedSearch/filters/BooleanFilter";
import { DateFilter } from "components/NewAdvancedSearch/filters/DateFilter";
import { ListFilter } from "components/NewAdvancedSearch/filters/ListFilter";
import { RangeFilter } from "components/NewAdvancedSearch/filters/RangeFilter";
import { SelectFilter } from "components/NewAdvancedSearch/filters/SelectFilter";
import { TextFilter } from "components/NewAdvancedSearch/filters/TextFilter";

// Match-any (OR) vs match-all (AND) across the selected values, serialized to
// the backend's `or_filters` param. This mode is exclusive to custom-field
// multi-selects — native selects never expose it.
const MATCH_MODES = [
  { value: "any", short: "Any", text: "Match any (OR)" },
  { value: "all", short: "All", text: "Match all (AND)" },
];

function MatchModeSelect({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-label="Match mode"
        className="tw:w-20 tw:bg-white tw:dark:bg-background"
      >
        <SelectValue placeholder="Mode">
          {mode => MATCH_MODES.find(m => m.value === mode)?.short}
        </SelectValue>
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false} className="tw:w-44">
        <SelectGroup>
          {MATCH_MODES.map(mode => (
            <SelectItem key={mode.value} value={mode.value}>
              {mode.text}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

// A custom-field `select` stores its choices and cardinality in `field_options`
// (carried on the descriptor as `options`). Adapt that to a normal SelectFilter
// descriptor so single/multi and the option list flow through unchanged. Multi
// selects additionally get the match-mode toggle above.
function SelectEditor({ descriptor, value, onChange }) {
  const fieldOptions = descriptor.options ?? {};
  const isSingle = fieldOptions.type !== "multi";
  const selectDescriptor = {
    ...descriptor,
    data: fieldOptions.options ?? [],
    isSingle,
  };

  const select = (
    <SelectFilter
      descriptor={selectDescriptor}
      value={value}
      onChange={onChange}
    />
  );

  if (isSingle) return select;

  // Default to Match all so the emitted query keeps the backend's AND default
  // when the toggle is never touched.
  const current = value ?? { op: "in", value: [], matchAll: true };
  const matchAll = current.matchAll ?? true;

  return (
    <div className="tw:flex tw:flex-1 tw:items-center tw:justify-end tw:gap-2">
      <MatchModeSelect
        value={matchAll ? "all" : "any"}
        onChange={mode => onChange({ ...current, matchAll: mode === "all" })}
      />
      {select}
    </div>
  );
}

// A custom-field `list` is a free-text multi-value input. Like multi-selects it
// gets the match-mode toggle, since the backend OR/ANDs its values through the
// same `or_filters` mechanism. The value is wrapped as `{ value, matchAll }` so
// the mode rides alongside the entered values.
function ListEditor({ value, onChange }) {
  const current = value ?? { value: [], matchAll: true };
  const values = current.value ?? [];
  const matchAll = current.matchAll ?? true;

  return (
    <div className="tw:flex tw:flex-1 tw:items-center tw:justify-end tw:gap-2">
      <MatchModeSelect
        value={matchAll ? "all" : "any"}
        onChange={mode => onChange({ ...current, matchAll: mode === "all" })}
      />
      <ListFilter
        value={values}
        onChange={next => onChange({ ...current, value: next })}
      />
    </div>
  );
}

const CF_EDITORS = {
  text: TextFilter,
  numeric: RangeFilter,
  boolean: BooleanFilter,
  date: DateFilter,
  select: SelectEditor,
  list: ListEditor,
};

export function CustomFieldValueEditor({ descriptor, value, onChange }) {
  const Editor = CF_EDITORS[descriptor.type] ?? TextFilter;
  return <Editor descriptor={descriptor} value={value} onChange={onChange} />;
}
